import { CARS, CAR_BY_ID, cloneTuning } from '../shared/cars.js';
import { WEATHER_VALUES } from '../shared/types.js';
import type { ArcadePreset, CrewModule, NpcDifficulty, PlayerIdentity, RaceEventMode, RacePhase, RaceWagerKind, TimeOfDay, VisualTuning, Weather } from '../shared/types.js';
import { normaliseCrewModule } from '../shared/crew-modules.js';
import { normaliseArcadePreset } from '../shared/arcade-presets.js';
import {
  browserLocalStorage,
  browserSessionStorage,
  CodecRepository,
  restoreStorageValue,
  SYSTEM_PERSISTENCE_CLOCK,
  type Codec,
  type PersistenceClock,
} from './persistence.js';

export type EnvironmentPreference<T extends string> = T | 'map';
export type NpcDifficultyPreference = NpcDifficulty | 'adaptive';

export interface DriverProfile {
  version: 1;
  name: string;
  selectedCarId: string;
  selectedMapId: string;
  npcCount: number;
  npcDifficulty: NpcDifficultyPreference;
  offlineMode: boolean;
  antiCheat: boolean;
  collisions: boolean;
  traffic: boolean;
  timeTrial: boolean;
  eventMode: RaceEventMode;
  arcadePreset: ArcadePreset;
  crewModule: CrewModule;
  wager: RaceWagerKind | 'none';
  timeOfDay: EnvironmentPreference<TimeOfDay>;
  weather: EnvironmentPreference<Weather>;
  tuning: Record<string, VisualTuning>;
  updatedAt: number;
}

export interface StoredRaceSession {
  version: 1;
  roomId: string;
  player: PlayerIdentity;
  inviteToken: string;
  mapId: string;
  mapName: string;
  phase: RacePhase;
  savedAt: number;
  /**
   * The server's sliding session lifetime when this pass was saved. Rooms are
   * gone once this long has passed without a save, so the garage stops
   * offering a resume the server can no longer honour.
   */
  resumeWindowMs?: number;
}

const PROFILE_KEY = 'nightshift:driver-profile:v1';
const LAST_RACE_KEY = 'nightshift:last-race';
const RACE_KEY_PREFIX = 'nightshift:race:v1:';
const LEGACY_NAME_KEY = 'nightshift:name';
// Upper bound for clock skew and for a server-reported session lifetime.
const RECENT_RACE_MAX_AGE = 24 * 60 * 60 * 1_000;
// Matches the server's default SESSION_TTL_HOURS for passes saved before
// servers reported their lifetime.
const DEFAULT_RESUME_WINDOW_MS = 6 * 60 * 60 * 1_000;
const MIN_RESUME_WINDOW_MS = 60_000;
const RECENT_RACE_FUTURE_SKEW_MS = 60_000;
const DIFFICULTIES = new Set<NpcDifficultyPreference>(['rookie', 'sport', 'pro', 'adaptive']);
const TIMES = new Set<DriverProfile['timeOfDay']>(['map', 'dawn', 'day', 'sunset', 'night']);
const WEATHER = new Set<DriverProfile['weather']>(['map', ...WEATHER_VALUES]);
const EVENT_MODES = new Set<RaceEventMode>(['circuit', 'sprint', 'time-trial', 'style-rush', 'elimination', 'heat-run', 'road-rage']);
const WAGERS = new Set<DriverProfile['wager']>(['none', 'clean-finish', 'clean-passes', 'extreme-route']);
const PHASES = new Set<RacePhase>(['lobby', 'countdown', 'racing', 'finished']);
const SPOILERS = new Set<VisualTuning['spoiler']>(['none', 'ducktail', 'gt']);
const WHEELS = new Set<VisualTuning['wheels']>(['turbine', 'mesh', 'five-spoke']);
const DECALS = new Set<VisualTuning['decal']>(['none', 'center-stripe', 'twin-stripe', 'chevron', 'circuit']);

export function createDefaultDriverProfile(name = '', now = Date.now()): DriverProfile {
  return {
    version: 1,
    name: validName(name),
    selectedCarId: CARS[0]?.id ?? '',
    selectedMapId: '',
    npcCount: 5,
    npcDifficulty: 'rookie',
    offlineMode: false,
    antiCheat: false,
    collisions: true,
    traffic: false,
    timeTrial: false,
    eventMode: 'circuit',
    arcadePreset: 'standard',
    crewModule: 'balanced',
    wager: 'none',
    timeOfDay: 'map',
    weather: 'map',
    tuning: Object.fromEntries(CARS.map((car) => [car.id, cloneTuning(car.defaultTuning)])),
    updatedAt: now,
  };
}

export function parseDriverProfile(raw: string | null, legacyName = '', now = Date.now()): DriverProfile {
  const fallback = createDefaultDriverProfile(legacyName, now);
  if (!raw) return fallback;
  try {
    const candidate = JSON.parse(raw) as unknown;
    if (!isRecord(candidate)) return fallback;
    const carId = typeof candidate.selectedCarId === 'string' && CAR_BY_ID.has(candidate.selectedCarId)
      ? candidate.selectedCarId
      : fallback.selectedCarId;
    const tuningSource = isRecord(candidate.tuning) ? candidate.tuning : {};
    const tuning = Object.fromEntries(CARS.map((car) => [
      car.id,
      normalizeTuning(tuningSource[car.id], car.defaultTuning),
    ]));
    const legacyTimeTrial = booleanValue(candidate.timeTrial, fallback.timeTrial);
    const eventMode = EVENT_MODES.has(candidate.eventMode as RaceEventMode)
      ? candidate.eventMode as RaceEventMode
      : legacyTimeTrial ? 'time-trial' : fallback.eventMode;
    return {
      version: 1,
      name: validName(candidate.name) || fallback.name,
      selectedCarId: carId,
      selectedMapId: validIdentifier(candidate.selectedMapId),
      npcCount: clampInteger(candidate.npcCount, 0, 11, fallback.npcCount),
      npcDifficulty: DIFFICULTIES.has(candidate.npcDifficulty as NpcDifficultyPreference)
        ? candidate.npcDifficulty as NpcDifficultyPreference
        : fallback.npcDifficulty,
      offlineMode: booleanValue(candidate.offlineMode, fallback.offlineMode),
      antiCheat: booleanValue(candidate.antiCheat, fallback.antiCheat),
      collisions: booleanValue(candidate.collisions, fallback.collisions),
      traffic: booleanValue(candidate.traffic, fallback.traffic),
      timeTrial: eventMode === 'time-trial',
      eventMode,
      arcadePreset: normaliseArcadePreset(candidate.arcadePreset),
      crewModule: normaliseCrewModule(candidate.crewModule),
      wager: WAGERS.has(candidate.wager as DriverProfile['wager'])
        ? candidate.wager as DriverProfile['wager']
        : fallback.wager,
      timeOfDay: TIMES.has(candidate.timeOfDay as DriverProfile['timeOfDay'])
        ? candidate.timeOfDay as DriverProfile['timeOfDay']
        : fallback.timeOfDay,
      weather: WEATHER.has(candidate.weather as DriverProfile['weather'])
        ? candidate.weather as DriverProfile['weather']
        : fallback.weather,
      tuning,
      updatedAt: finiteNumber(candidate.updatedAt, fallback.updatedAt),
    };
  } catch {
    return fallback;
  }
}

export function loadDriverProfile(clock: PersistenceClock = SYSTEM_PERSISTENCE_CLOCK): DriverProfile {
  const legacyName = readLocal(LEGACY_NAME_KEY) ?? '';
  return driverProfileRepository(legacyName, clock).load()
    ?? createDefaultDriverProfile(legacyName, clock.now());
}

export function saveDriverProfile(
  profile: DriverProfile,
  clock: PersistenceClock = SYSTEM_PERSISTENCE_CLOCK,
): void {
  driverProfileRepository('', clock).save({ ...profile, version: 1, updatedAt: clock.now() });
}

export function parseRaceSession(raw: string | null, expectedRoomId = ''): StoredRaceSession | null {
  if (!raw) return null;
  try {
    const candidate = JSON.parse(raw) as unknown;
    if (
      !isRecord(candidate)
      || candidate.version !== 1
      || !isRecord(candidate.player)
      || !PHASES.has(candidate.phase as RacePhase)
      || typeof candidate.savedAt !== 'number'
      || !Number.isFinite(candidate.savedAt)
    ) return null;
    const roomId = validIdentifier(candidate.roomId);
    const expected = validIdentifier(expectedRoomId);
    if (!roomId || (expected && roomId !== expected)) return null;
    const inviteToken = validToken(candidate.inviteToken);
    const playerSource = candidate.player;
    const carId = typeof playerSource.carId === 'string' && CAR_BY_ID.has(playerSource.carId)
      ? playerSource.carId
      : '';
    const car = CAR_BY_ID.get(carId);
    const playerId = validIdentifier(playerSource.id);
    const playerToken = validToken(playerSource.token);
    const name = validName(playerSource.name);
    if (!roomId || !inviteToken || !car || !playerId || !playerToken || !name) return null;
    return {
      version: 1,
      roomId,
      inviteToken,
      player: {
        id: playerId,
        token: playerToken,
        name,
        carId,
        tuning: normalizeTuning(playerSource.tuning, car.defaultTuning),
        crewModule: normaliseCrewModule(playerSource.crewModule),
      },
      mapId: validIdentifier(candidate.mapId),
      mapName: typeof candidate.mapName === 'string' ? candidate.mapName.trim().slice(0, 80) : '',
      phase: candidate.phase as RacePhase,
      savedAt: candidate.savedAt,
      ...(validResumeWindow(candidate.resumeWindowMs) ? { resumeWindowMs: candidate.resumeWindowMs } : {}),
    };
  } catch {
    return null;
  }
}

export function saveRaceSession(
  session: StoredRaceSession,
  clock: PersistenceClock = SYSTEM_PERSISTENCE_CLOCK,
): boolean {
  const normalized = parseRaceSession(JSON.stringify({ ...session, version: 1, savedAt: clock.now() }), session.roomId);
  if (!normalized) return false;
  const sessionKey = `${RACE_KEY_PREFIX}${normalized.roomId}`;
  const previousSession = readLocal(sessionKey);
  const previousPointer = readLocal(LAST_RACE_KEY);
  if (!writeLocal(sessionKey, JSON.stringify(normalized))) return false;
  if (writeLocal(LAST_RACE_KEY, normalized.roomId)) return true;

  // localStorage has no multi-key transaction. Restore both keys to their
  // exact previous values so a failed pointer commit cannot leak a successor
  // bearer token which the UI can no longer reach.
  restoreLocal(sessionKey, previousSession);
  restoreLocal(LAST_RACE_KEY, previousPointer);
  return false;
}

/**
 * Commits the successor reconnect pass before removing its predecessor. A
 * quota/private-mode write failure therefore retains the last known-good pass.
 */
export function replaceRaceSession(
  previousRoomId: string,
  session: StoredRaceSession,
): boolean {
  if (!saveRaceSession(session)) return false;
  if (previousRoomId !== session.roomId) forgetRaceSession(previousRoomId);
  return true;
}

export function loadRaceSession(roomId: string): StoredRaceSession | null {
  const validRoomId = validIdentifier(roomId);
  if (!validRoomId) return null;
  const key = `${RACE_KEY_PREFIX}${validRoomId}`;
  const stored = parseRaceSession(readLocal(key), validRoomId);
  if (stored) return stored;
  removeLocal(key);

  // Migrate the previous tab-only identity without ever exposing its bearer
  // tokens in page content or URLs beyond the existing private invite link.
  const legacy = parseLegacyRaceSession(readSession(`nightshift:room:${validRoomId}`), validRoomId);
  if (legacy) saveRaceSession(legacy);
  return legacy;
}

export function loadRecentRaceSession(now = Date.now()): StoredRaceSession | null {
  pruneStaleRaceSessions(now);
  const storedRoomId = readLocal(LAST_RACE_KEY);
  if (!storedRoomId) return null;
  const roomId = validIdentifier(storedRoomId);
  if (!roomId) {
    removeLocal(LAST_RACE_KEY);
    return null;
  }
  const session = loadRaceSession(roomId);
  const futureOffset = session ? session.savedAt - now : 0;
  if (
    !session
    || session.phase === 'finished'
    || futureOffset > RECENT_RACE_MAX_AGE
    || now - session.savedAt > raceSessionResumeWindow(session)
  ) {
    forgetRaceSession(roomId);
    return null;
  }
  if (futureOffset > RECENT_RACE_FUTURE_SKEW_MS) {
    // Android and manually managed tablets can move their wall clock backwards
    // after a race was saved. Keep plausible same-day credentials and repair
    // their ordering timestamp instead of deleting a valid reconnect pass.
    const normalised = { ...session, savedAt: now };
    void saveRaceSession(normalised);
    return normalised;
  }
  return session;
}

/**
 * Drops reconnect passes for rooms other than the most recent one once they
 * are older than their resume window. Only the last race is ever offered for
 * resume, and the server has already expired those rooms, so older bearer
 * tokens would otherwise accumulate in localStorage forever.
 */
export function pruneStaleRaceSessions(now = Date.now()): void {
  let keys: string[];
  try {
    if (typeof localStorage === 'undefined') return;
    keys = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(RACE_KEY_PREFIX)) keys.push(key);
    }
  } catch {
    return;
  }
  const lastRoomId = readLocal(LAST_RACE_KEY);
  for (const key of keys) {
    const roomId = key.slice(RACE_KEY_PREFIX.length);
    if (roomId === lastRoomId) continue;
    const session = parseRaceSession(readLocal(key), roomId);
    if (
      session
      && session.savedAt - now <= RECENT_RACE_MAX_AGE
      && now - session.savedAt <= raceSessionResumeWindow(session)
    ) continue;
    forgetRaceSession(roomId);
    removeLocal(key);
  }
}

export function updateRaceSession(
  roomId: string,
  updates: Partial<Pick<StoredRaceSession, 'mapId' | 'mapName' | 'phase' | 'resumeWindowMs'>>,
): StoredRaceSession | null {
  const session = loadRaceSession(roomId);
  if (!session) return null;
  const updated = { ...session, ...updates, savedAt: Date.now() };
  if (updated.resumeWindowMs === undefined) delete updated.resumeWindowMs;
  return saveRaceSession(updated) ? updated : null;
}

/** How long after its last save a reconnect pass can still resume a room. */
export function raceSessionResumeWindow(session: Pick<StoredRaceSession, 'resumeWindowMs'>): number {
  return session.resumeWindowMs ?? DEFAULT_RESUME_WINDOW_MS;
}

/** Clamps a server-reported session lifetime into a storable resume window. */
export function resumeWindowFromSessionTtl(sessionTtlMs: number | undefined): number | undefined {
  if (typeof sessionTtlMs !== 'number' || !Number.isFinite(sessionTtlMs)) return undefined;
  return Math.round(Math.min(RECENT_RACE_MAX_AGE, Math.max(MIN_RESUME_WINDOW_MS, sessionTtlMs)));
}

function validResumeWindow(value: unknown): value is number {
  return typeof value === 'number'
    && Number.isInteger(value)
    && value >= MIN_RESUME_WINDOW_MS
    && value <= RECENT_RACE_MAX_AGE;
}

export function forgetRaceSession(roomId: string): void {
  const validRoomId = validIdentifier(roomId);
  if (!validRoomId) return;
  removeLocal(`${RACE_KEY_PREFIX}${validRoomId}`);
  if (readLocal(LAST_RACE_KEY) === validRoomId) removeLocal(LAST_RACE_KEY);
  browserSessionStorage().remove(`nightshift:room:${validRoomId}`);
}

function normalizeTuning(value: unknown, fallback: VisualTuning): VisualTuning {
  const candidate = isRecord(value) ? value : {};
  return {
    paint: validColour(candidate.paint, fallback.paint),
    accent: validColour(candidate.accent, fallback.accent),
    rims: validColour(candidate.rims, fallback.rims),
    neon: validColour(candidate.neon, fallback.neon),
    neonEnabled: typeof candidate.neonEnabled === 'boolean' ? candidate.neonEnabled : fallback.neonEnabled,
    spoiler: SPOILERS.has(candidate.spoiler as VisualTuning['spoiler'])
      ? candidate.spoiler as VisualTuning['spoiler']
      : fallback.spoiler,
    wheels: WHEELS.has(candidate.wheels as VisualTuning['wheels'])
      ? candidate.wheels as VisualTuning['wheels']
      : fallback.wheels,
    decal: DECALS.has(candidate.decal as VisualTuning['decal'])
      ? candidate.decal as VisualTuning['decal']
      : fallback.decal,
    stance: Math.max(-0.08, Math.min(0.08, finiteNumber(candidate.stance, fallback.stance))),
  };
}

function parseLegacyRaceSession(raw: string | null, expectedRoomId: string): StoredRaceSession | null {
  if (!raw) return null;
  try {
    const candidate = JSON.parse(raw) as unknown;
    if (!isRecord(candidate)) return null;
    return parseRaceSession(JSON.stringify({
      ...candidate,
      version: 1,
      roomId: expectedRoomId,
      mapId: validIdentifier(candidate.mapId),
      mapName: typeof candidate.mapName === 'string' ? candidate.mapName : '',
      phase: PHASES.has(candidate.phase as RacePhase) ? candidate.phase : 'lobby',
      savedAt: Date.now(),
    }), expectedRoomId);
  } catch {
    return null;
  }
}

function validColour(value: unknown, fallback: string): string {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value : fallback;
}

function validName(value: unknown): string {
  return typeof value === 'string' ? value.trim().slice(0, 18) : '';
}

function validIdentifier(value: unknown): string {
  return typeof value === 'string' && /^[a-zA-Z0-9_-]{1,100}$/.test(value) ? value : '';
}

function validToken(value: unknown): string {
  return typeof value === 'string' && /^[a-zA-Z0-9_-]{8,160}$/.test(value) ? value : '';
}

function finiteNumber(value: unknown, fallback: number): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function booleanValue(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function clampInteger(value: unknown, minimum: number, maximum: number, fallback: number): number {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(minimum, Math.min(maximum, Math.round(number))) : fallback;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function readLocal(key: string): string | null {
  return browserLocalStorage().read(key);
}

function writeLocal(key: string, value: string): boolean {
  return browserLocalStorage().write(key, value);
}

function removeLocal(key: string): void {
  browserLocalStorage().remove(key);
}

function restoreLocal(key: string, value: string | null): void {
  restoreStorageValue(browserLocalStorage(), key, value);
}

function readSession(key: string): string | null {
  return browserSessionStorage().read(key);
}

function driverProfileRepository(
  legacyName: string,
  clock: PersistenceClock,
): CodecRepository<DriverProfile> {
  const codec: Codec<DriverProfile> = {
    encode: (profile) => JSON.stringify(profile),
    decode: (raw) => parseDriverProfile(raw, legacyName, clock.now()),
  };
  return new CodecRepository(browserLocalStorage(), PROFILE_KEY, codec);
}
